
/* eslint-disable  */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import gfm from 'remark-gfm';
import { useParams, useLocation } from 'react-router-dom';
import Page from '../../components/Page';
import adminServices from '../../services/adminServices';
import { IC, PREVIEW_CSS, processCustomBlocks } from './ebookShared';
import EbookTOC from './EbookTOC';
import { AIDrawer, HighlightsPanel, NotesPanel, BookmarksPanel } from './EbookDrawers';
import { AnalyticsModal, CompletionModal } from './EbookModals';
import { useReadingTracker } from './useReadingTracker';

// ── Theme & font settings persisted to localStorage ──────────────────────────
const SETTINGS_KEY = 'ebook_reader_settings';
const DEFAULT_SETTINGS = { theme: 'light', fontSize: 16, fontFamily: 'sans' };

function loadSettings() {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    return raw ? { ...DEFAULT_SETTINGS, ...JSON.parse(raw) } : DEFAULT_SETTINGS;
  } catch {
    return DEFAULT_SETTINGS;
  }
}

function Ico({ d, size, cls }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size || 16} height={size || 16}
      viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={cls || ''}>
      <path d={d} />
    </svg>
  );
}

function fmtTime(sec) {
  if (!sec || isNaN(sec)) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

// ── Inline Audio Player ───────────────────────────────────────────────────────
function AudioPlayer({ audioUrl, timings, onTimeUpdate, theme }) {
  const th = theme;
  const audioRef = useRef(null);
  const [playing, setPlaying]   = useState(false);
  const [current, setCurrent]   = useState(0);
  const [duration, setDuration] = useState(0);
  const [rate, setRate]         = useState(1);

  useEffect(() => {
    setPlaying(false);
    setCurrent(0);
    setDuration(0);
  }, [audioUrl]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else         { a.play();  setPlaying(true);  }
  };

  const seek = (e) => {
    const a = audioRef.current;
    if (!a || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct  = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
    a.currentTime = pct * duration;
    setCurrent(pct * duration);
  };

  const changeRate = (r) => {
    setRate(r);
    if (audioRef.current) audioRef.current.playbackRate = r;
  };

  const progress = duration ? (current / duration) * 100 : 0;

  return (
    <div className="flex items-center gap-2 flex-1 min-w-0 px-2.5 py-1.5 rounded-xl"
      style={{ background: th.bg === '#0f172a' ? '#1e293b' : '#f1f5f9', border: `1px solid ${th.border}` }}>
      <audio
        ref={audioRef}
        src={audioUrl}
        onLoadedMetadata={(e) => setDuration(e.target.duration)}
        onTimeUpdate={(e) => {
          setCurrent(e.target.currentTime);
          onTimeUpdate && onTimeUpdate(e.target.currentTime, timings);
        }}
        onEnded={() => {
          setPlaying(false);
          setCurrent(0);
          onTimeUpdate && onTimeUpdate(0, timings);
        }}
        preload="metadata"
      />
      <button onClick={toggle}
        className="flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center"
        style={{ background: '#6366f1', color: '#fff' }}>
        {playing ? (
          <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
            <rect x="6" y="4" width="4" height="16" rx="1" />
            <rect x="14" y="4" width="4" height="16" rx="1" />
          </svg>
        ) : (
          <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
            <polygon points="5,3 19,12 5,21" />
          </svg>
        )}
      </button>
      <span className="flex-shrink-0 tabular-nums" style={{ fontSize: 11, color: th.text, opacity: 0.5, minWidth: 32 }}>
        {fmtTime(current)}
      </span>
      <div className="flex-1 min-w-0 relative cursor-pointer"
        style={{ height: 4, borderRadius: 9999, background: th.border }}
        onClick={seek}>
        <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', borderRadius: 9999, width: `${progress}%`, background: '#6366f1', transition: 'width 0.1s linear' }} />
      </div>
      <span className="flex-shrink-0 tabular-nums" style={{ fontSize: 11, color: th.text, opacity: 0.5, minWidth: 32 }}>
        {fmtTime(duration)}
      </span>
      <select value={rate} onChange={(e) => changeRate(Number(e.target.value))}
        className="flex-shrink-0 rounded-lg border outline-none cursor-pointer"
        style={{ background: 'transparent', color: th.text, borderColor: th.border, fontSize: 11, padding: '2px 4px' }}>
        {[0.5, 0.75, 1, 1.25, 1.5, 2].map((r) => (
          <option key={r} value={r}>{r}×</option>
        ))}
      </select>
    </div>
  );
}

// ── Highlight colours (shared with the Highlights panel) ─────────────────────
const HL_COLORS = ['#FCD34D', '#F472B6', '#86EFAC', '#93C5FD', '#D8B4FE', '#FDBA74'];

const HL_CSS = `
mark.ebk-hl{color:inherit;border-radius:3px;padding:0 1px;box-decoration-break:clone;-webkit-box-decoration-break:clone;}
mark.tts-hl{background:#bfdbfe;color:inherit;border-radius:3px;padding:0 2px;transition:background 0.2s;}
`;

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// ── DOM highlighter ─────────────────────────────────────────────────────────
// String-replacing the markdown source only ever matched a highlight that
// happened to be one clean run of text — a selection crossing **bold**, a
// link, or a paragraph break never matched, so only the "starting point"
// showed. Instead we walk the *rendered* text nodes, match against the
// visible text (whitespace-normalised) and wrap each intersecting slice in a
// <mark>. Handles selections that span multiple elements.
function normWs(s) { return s.replace(/\s+/g, ' '); }

function unwrapHighlights(host) {
  host.querySelectorAll('mark.ebk-hl').forEach((m) => {
    const p = m.parentNode;
    while (m.firstChild) p.insertBefore(m.firstChild, m);
    p.removeChild(m);
    p.normalize();
  });
}

function highlightOne(host, rawNeedle, color) {
  const needle = normWs(rawNeedle).trim();
  if (needle.length < 2) return;

  // Collect text nodes + a normalised string with a map back to (node, offset).
  const walker = document.createTreeWalker(host, NodeFilter.SHOW_TEXT, {
    acceptNode: (n) => (n.nodeValue && !n.parentElement?.closest('mark'))
      ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT,
  });
  let norm = '';
  const map = [];               // map[i] = { node, offset } for norm[i]
  let node;
  let prevWasSpace = true;      // trim leading collapsed space
  while ((node = walker.nextNode())) {
    const v = node.nodeValue;
    for (let i = 0; i < v.length; i++) {
      const isSpace = /\s/.test(v[i]);
      if (isSpace) {
        if (prevWasSpace) continue;
        norm += ' '; map.push({ node, offset: i }); prevWasSpace = true;
      } else {
        norm += v[i]; map.push({ node, offset: i }); prevWasSpace = false;
      }
    }
  }

  let from = 0;
  for (;;) {
    const idx = norm.indexOf(needle, from);
    if (idx === -1) break;
    const startPos = map[idx];
    const endPos   = map[idx + needle.length - 1];
    from = idx + needle.length;
    if (!startPos || !endPos) continue;

    const range = document.createRange();
    try {
      range.setStart(startPos.node, startPos.offset);
      range.setEnd(endPos.node, endPos.offset + 1);
    } catch { continue; }

    // Wrap every text node the range touches, slice by slice.
    const slices = [];
    const tw = document.createTreeWalker(host, NodeFilter.SHOW_TEXT, null);
    let t;
    while ((t = tw.nextNode())) {
      if (!range.intersectsNode(t)) continue;
      const s = t === range.startContainer ? range.startOffset : 0;
      const e = t === range.endContainer   ? range.endOffset   : t.nodeValue.length;
      if (e > s) slices.push({ t, s, e });
    }
    slices.forEach(({ t, s, e }) => {
      const r = document.createRange();
      r.setStart(t, s); r.setEnd(t, e);
      const mk = document.createElement('mark');
      mk.className = 'ebk-hl';
      mk.style.background = color;
      try { r.surroundContents(mk); } catch { /* skip odd ranges */ }
    });
    break; // one match per highlight is enough
  }
}

// ── Content renderer ──────────────────────────────────────────────────────────
function ContentRenderer({ content, activeIndex, timings }) {
  let out = content;

  if (timings && timings.length && activeIndex >= 0) {
    const sentence = timings[activeIndex]?.text;
    if (sentence) {
      out = out.replace(new RegExp(escapeRe(sentence), 'g'), `<mark class="tts-hl">${sentence}</mark>`);
    }
  }

  return (
    <div>
      <style>{PREVIEW_CSS}{HL_CSS}</style>
      <div className="ebk px-4 sm:px-8 py-6 max-w-4xl mx-auto">
        <ReactMarkdown plugins={[gfm]} allowDangerousHtml>{processCustomBlocks(out)}</ReactMarkdown>
      </div>
    </div>
  );
}

// ── Offline indicator ─────────────────────────────────────────────────────────
function OfflineBanner() {
  const [offline, setOffline] = useState(!navigator.onLine);
  useEffect(() => {
    const on  = () => setOffline(false);
    const off = () => setOffline(true);
    window.addEventListener('online',  on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);
  if (!offline) return null;
  return (
    <div className="flex-shrink-0 flex items-center justify-center gap-2 py-1.5 text-xs font-bold"
      style={{ background: '#fef3c7', color: '#92400e' }}>
      <span>⚠️</span>
      <span>You're offline — your progress will be saved when you reconnect</span>
    </div>
  );
}

// ── Resume toast ──────────────────────────────────────────────────────────────
function ResumeToast({ show, onDismiss }) {
  useEffect(() => {
    if (!show) return;
    const t = setTimeout(onDismiss, 4000);
    return () => clearTimeout(t);
  }, [show, onDismiss]);
  if (!show) return null;
  return (
    <div className="fixed bottom-20 left-1/2 z-50 -translate-x-1/2 flex items-center gap-2 px-4 py-2.5 rounded-2xl shadow-xl text-xs font-bold"
      style={{ background: '#1e293b', color: '#e2e8f0' }}>Resuming from where you left off </div>
  );
}

// ── Main EBookReader ──────────────────────────────────────────────────────────
export default function EBookReader() {
  const { ebookId } = useParams();
  const location    = useLocation();
  // Optional deep-link: /e-book/:id?topicId=123 opens straight on that topic
  // (used by the "Read Topic" buttons on the e-book detail Curriculum tab).
  const wantedTopicId = new URLSearchParams(location.search).get('topicId');

  const [book, setBook]             = useState(null);
  const [chapters, setChapters]     = useState([]);
  const [analytics, setAnalytics]   = useState(null);
  const [highlights, setHighlights] = useState([]);
  const [notes, setNotes]           = useState([]);
  const [bookmarks, setBookmarks]   = useState([]);
  const [topicContent, setTopicContent] = useState('');
  const [activeTopic, setActiveTopic]   = useState(null);
  const [loadingToc, setLoadingToc]         = useState(true);
  const [loadingContent, setLoadingContent] = useState(false);
  const [tocOpen, setTocOpen]               = useState(false);
  const [expandedChapters, setExpandedChapters] = useState({});
  const [drawer, setDrawer]             = useState(null);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [selectionMenu, setSelectionMenu] = useState(null);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showCompletion, setShowCompletion] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showResumeToast, setShowResumeToast] = useState(false);

  // ── Settings — loaded from localStorage ────────────────────────────────────
  const [settings, setSettings] = useState(loadSettings);

  // Persist settings to localStorage on every change
  useEffect(() => {
    try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings)); } catch {}
  }, [settings]);

  // TTS
  const [audioUrl, setAudioUrl]       = useState(null);
  const [timings, setTimings]         = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);

  const handleTimeUpdate = useCallback((currentTime, tmgs) => {
    if (!tmgs || !tmgs.length) return;
    let idx = -1;
    for (let i = 0; i < tmgs.length; i++) {
      if (tmgs[i].timeSeconds <= currentTime) idx = i;
      else break;
    }
    setActiveIndex(idx);
  }, []);

  const contentRef = useRef(null);

  // ── Save function passed to tracker ────────────────────────────────────────
  const saveFn = useCallback(async (payload) => {
    await adminServices.upsertReadingProgress({
      topicId:          payload.topicId,
      status:           payload.status,
      timeSpentSeconds: payload.timeSpentSeconds,
      scrollPercent:    payload.scrollPercent,
      recordedAt:       payload.recordedAt,   // backend uses this to reject stale writes
    });
  }, []);

  // ── Auto-complete callback ──────────────────────────────────────────────────
  const handleAutoComplete = useCallback((topic) => {
    setActiveTopic((prev) => prev?.topicId === topic.topicId ? { ...prev, status: 'completed' } : prev);
    setChapters((prev) => prev.map((ch) => ({
      ...ch,
      topics: ch.topics.map((t) => t.topicId === topic.topicId ? { ...t, status: 'completed' } : t),
    })));
    setShowCompletion(true);
  }, []);

  // ── Tracking hook ───────────────────────────────────────────────────────────
  const { saveNow } = useReadingTracker({
    activeTopic,
    contentRef,
    onAutoComplete: handleAutoComplete,
    saveFn,
  });

  // ── Select topic ───────────────────────────────────────────────────────────
  const selectTopic = useCallback((topic) => {
    setActiveTopic(topic);
    setTopicContent('');
    setHighlights([]);
    setNotes([]);
    setAudioUrl(null);
    setTimings([]);
    setActiveIndex(-1);
    setLoadingContent(true);

    adminServices.getStudentTopic(topic.topicId)
      .then((res) => {
        const d = res?.data;
        setTopicContent(d?.rawMarkdown || '');
        if (d?.ebookTitle) setBook((p) => p || { title: d.ebookTitle });
        if (d?.tts_audio_url) {
          setAudioUrl(d.tts_audio_url);
          setTimings(Array.isArray(d?.tts_timings) ? d.tts_timings : []);
        }
        // Show resume toast if they have previous scroll progress
        if ((d?.scrollPercent || topic.scrollPercent) > 5) {
          setShowResumeToast(true);
        }
      })
      .catch(() => setTopicContent(''))
      .finally(() => setLoadingContent(false));

    adminServices.getTopicHighlights(topic.topicId)
      .then((res) => setHighlights(Array.isArray(res?.data) ? res.data : [])).catch(() => {});

    adminServices.getTopicNotes(topic.topicId)
      .then((res) => setNotes(Array.isArray(res?.data) ? res.data : [])).catch(() => {});

    adminServices.getStudentBookmarks()
      .then((res) => {
        const bms = Array.isArray(res?.data) ? res.data : [];
        setBookmarks(bms);
        setIsBookmarked(bms.some((b) => String(b.topicId) === String(topic.topicId)));
      }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!ebookId) return;
    setLoadingToc(true);

    adminServices.getPublishedEbooks()
      .then((res) => {
        const found = (Array.isArray(res?.data) ? res.data : []).find((e) => String(e.id) === String(ebookId));
        if (found) setBook(found);
      }).catch(() => {});

    adminServices.getEbookTOC(ebookId)
      .then((res) => {
        const rows = Array.isArray(res?.data) ? res.data : [];
        const chMap = {};
        rows.forEach((r) => {
          if (!chMap[r.chapterId]) chMap[r.chapterId] = { chapterId: r.chapterId, chapterTitle: r.chapterTitle, chapterNumber: r.chapterNumber, topics: [] };
          chMap[r.chapterId].topics.push(r);
        });
        const chs = Object.values(chMap);
        setChapters(chs);
        if (chs.length > 0) {
          // A deep-linked topic wins; otherwise resume where the learner left
          // off: an in-progress topic first, then the first unfinished topic,
          // then the very first topic.
          const first = (wantedTopicId && rows.find((r) => String(r.topicId) === String(wantedTopicId)))
            || rows.find((r) => r.status === 'in_progress')
            || rows.find((r) => r.status !== 'completed')
            || rows[0];
          setExpandedChapters({ [first ? first.chapterId : chs[0].chapterId]: true });
          if (first) selectTopic(first);
        }
      }).catch(() => {}).finally(() => setLoadingToc(false));

    adminServices.getStudentEbookAnalytics(ebookId)
      .then((res) => setAnalytics(res?.data ?? null)).catch(() => {});
  }, [ebookId, selectTopic, wantedTopicId]);

  const allTopics  = chapters.flatMap((c) => c.topics);
  const topicIndex = allTopics.findIndex((t) => t.topicId === activeTopic?.topicId);

  // ── Mark complete (manual button) ─────────────────────────────────────────
  const markComplete = async () => {
    if (!activeTopic) return;
    // Already done — this is just a status badge now, don't re-hit the API.
    if (activeTopic.status === 'completed') return;
    await saveNow('completed');
    const updated = { ...activeTopic, status: 'completed' };
    setActiveTopic(updated);
    setChapters((prev) => prev.map((ch) => ({
      ...ch,
      topics: ch.topics.map((t) => t.topicId === activeTopic.topicId ? { ...t, status: 'completed' } : t),
    })));
    setShowCompletion(true);
  };

  const goNext = () => { if (topicIndex < allTopics.length - 1) selectTopic(allTopics[topicIndex + 1]); };
  const goPrev = () => { if (topicIndex > 0) selectTopic(allTopics[topicIndex - 1]); };

  // ── Optimistic mutations ─────────────────────────────────────────────────────
  // Update local state first so the highlight / note / bookmark shows instantly,
  // then fire the API and reconcile: swap the temp row for the saved one, or
  // roll it back if the request failed.
  const tmpId = () => `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
  const isTmp = (id) => typeof id === 'string' && id.startsWith('tmp_');

  const toggleBookmark = async () => {
    if (!activeTopic) return;
    if (isBookmarked) {
      const bm = bookmarks.find((b) => String(b.topicId) === String(activeTopic.topicId));
      setBookmarks((p) => p.filter((b) => String(b.topicId) !== String(activeTopic.topicId)));
      setIsBookmarked(false);
      if (bm && !isTmp(bm.id)) {
        adminServices.deleteStudentBookmark(bm.id).catch(() => {
          setBookmarks((p) => [...p, bm]); setIsBookmarked(true);
        });
      }
    } else {
      const id = tmpId();
      setBookmarks((p) => [...p, { id, topicId: activeTopic.topicId, topicTitle: activeTopic.topicTitle, chapterTitle: activeTopic.chapterTitle, _pending: true }]);
      setIsBookmarked(true);
      const res = await adminServices.createStudentBookmark({ topicId: activeTopic.topicId }).catch(() => null);
      if (res?.data) {
        setBookmarks((p) => p.map((b) => (b.id === id ? res.data : b)));
      } else {
        setBookmarks((p) => p.filter((b) => b.id !== id));
        setIsBookmarked(false);
      }
    }
  };

  const createHighlight = async (text, colorHex) => {
    if (!activeTopic) return;
    const id = tmpId();
    setHighlights((p) => [...p, { id, topicId: activeTopic.topicId, selectedText: text, colorHex, createdAt: new Date().toISOString(), _pending: true }]);
    const res = await adminServices.createStudentHighlight({ topicId: activeTopic.topicId, selectedText: text, colorHex }).catch(() => null);
    setHighlights((p) => (res?.data ? p.map((h) => (h.id === id ? res.data : h)) : p.filter((h) => h.id !== id)));
  };

  const createNote = async (data) => {
    if (!activeTopic) return;
    const id = tmpId();
    setNotes((p) => [{ id, ...data, topicId: activeTopic.topicId, _pending: true }, ...p]);
    const res = await adminServices.createStudentNote({ ...data, topicId: activeTopic.topicId }).catch(() => null);
    setNotes((p) => (res?.data ? p.map((n) => (n.id === id ? res.data : n)) : p.filter((n) => n.id !== id)));
  };

  // Paint saved highlights onto the rendered content (see highlightOne). Runs
  // after every content / highlight / TTS change since ReactMarkdown re-renders
  // wipe the injected <mark>s.
  useEffect(() => {
    const host = contentRef.current?.querySelector('.ebk');
    if (!host) return;
    unwrapHighlights(host);
    if (!highlights.length) return;
    const id = window.requestAnimationFrame(() => {
      [...highlights]
        .filter((h) => h.selectedText && h.selectedText.trim().length >= 2)
        .sort((a, b) => b.selectedText.trim().length - a.selectedText.trim().length)
        .forEach((h) => highlightOne(host, h.selectedText, h.colorHex || '#FCD34D'));
    });
    return () => window.cancelAnimationFrame(id);
  }, [highlights, topicContent, loadingContent, activeIndex]);

  const handleMouseUp = useCallback(() => {
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed || sel.toString().trim().length < 5) { setSelectionMenu(null); return; }
    const scroller = contentRef.current;
    if (!scroller) return;
    const text  = sel.toString().trim();
    const range = sel.getRangeAt(0);
    const rect  = range.getBoundingClientRect();
    const cr    = scroller.getBoundingClientRect();
    // The menu is absolutely positioned inside the scroll container, so its
    // offsets are measured from the top of the *scrollable content*, not the
    // visible area — add scrollTop/scrollLeft or it always lands at the top.
    const anchorTop  = rect.top  - cr.top  + scroller.scrollTop;
    const anchorLeft = rect.left - cr.left + scroller.scrollLeft + rect.width / 2;
    setSelectedText(text);
    setSelectionMenu({
      top:  Math.max(scroller.scrollTop + 8, anchorTop - 44),
      left: Math.min(Math.max(anchorLeft, 130), scroller.scrollLeft + scroller.clientWidth - 130),
    });
  }, []);

  useEffect(() => {
    document.addEventListener('mouseup', handleMouseUp);
    return () => document.removeEventListener('mouseup', handleMouseUp);
  }, [handleMouseUp]);

  const openDrawer = (type) => setDrawer((d) => (d === type ? null : type));

  const FONTS = {
    sans:  "'Inter', 'Helvetica Neue', sans-serif",
    serif: "'Georgia', 'Times New Roman', serif",
    mono:  "'Fira Code', 'Courier New', monospace",
  };
  const THEMES = {
    light: { bg: '#f8fafc', mainBg: '#ffffff', text: '#1e293b', border: '#e2e8f0', headerBg: '#ffffff' },
    dark:  { bg: '#0f172a', mainBg: '#1e293b', text: '#e2e8f0', border: '#334155', headerBg: '#1e293b' },
    sepia: { bg: '#f5f0e8', mainBg: '#faf6f0', text: '#3d2b1f', border: '#d4c4a8', headerBg: '#faf6f0' },
  };
  const th = THEMES[settings.theme];

  return (
    <Page title="E-Book Reader" style={{ paddingTop: '65px !important', paddingBottom: '0 !important' }}>
      {/* Fixed-height shell: the reader chrome (top bar + topic bar + prev/next)
          stays put and only the content column scrolls — the window itself
          never scrolls. 65px is the fixed app navbar above us. */}
      <div className="flex flex-col font-sans" style={{ height: 'calc(100dvh - 65px)', overflow: 'hidden', background: th.bg, color: th.text, fontFamily: FONTS[settings.fontFamily], fontSize: settings.fontSize, position: 'relative' }}>

        {/* OFFLINE BANNER */}
        <OfflineBanner />

        {/* RESUME TOAST */}
        <ResumeToast show={showResumeToast} onDismiss={() => setShowResumeToast(false)} />

        {/* TOP BAR */}
        <header className="flex-shrink-0 flex items-center gap-2 px-3 border-b z-20 shadow-sm" style={{ minHeight: 54, background: th.headerBg, borderColor: th.border }}>
          <button onClick={() => window.history.back()} className="p-1.5 rounded-lg hover:bg-slate-100 flex-shrink-0">
            <Ico d={IC.back} size={16} />
          </button>
          <button onClick={() => setTocOpen((p) => !p)} className="p-1.5 rounded-lg hover:bg-indigo-50 flex-shrink-0">
            <Ico d={IC.menu} size={16} />
          </button>
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Ico d={IC.book} size={15} cls="text-indigo-500 flex-shrink-0 hidden sm:block" />
            <p className="text-sm min-w-0 truncate">
              <span className="font-bold">{book?.title || 'Loading…'}</span>
              {activeTopic && (
                <>
                  <span className="opacity-40 hidden md:inline">{'  ·  '}{activeTopic.chapterTitle}{'  ›  '}</span>
                  <span className="font-semibold">{'  '}{activeTopic.topicTitle}</span>
                </>
              )}
            </p>
          </div>
          <div className="flex items-center gap-0.5 flex-shrink-0">
            {activeTopic && (
              <button onClick={markComplete}
                className={`flex-shrink-0 flex items-center gap-1 mr-1 px-3 py-1.5 rounded-xl text-xs font-bold ${activeTopic.status === 'completed' ? 'bg-emerald-100 text-emerald-700 cursor-default' : 'bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100'}`}>
                <Ico d={IC.check} size={12} />
                <span className="hidden sm:inline">{activeTopic.status === 'completed' ? 'Completed ✓' : 'Mark Complete'}</span>
              </button>
            )}
            <button onClick={toggleBookmark} className={`p-2 rounded-lg ${isBookmarked ? 'text-amber-500 bg-amber-50' : 'text-slate-400 hover:text-amber-500'}`}>
              <Ico d={IC.star} size={16} />
            </button>
            <button onClick={() => openDrawer('highlights')} className={`p-2 rounded-lg ${drawer === 'highlights' ? 'text-amber-600 bg-amber-50' : 'text-slate-400 hover:text-slate-600'}`}>
              <Ico d={IC.highlight} size={16} />
            </button>
            <button onClick={() => openDrawer('notes')} className={`p-2 rounded-lg ${drawer === 'notes' ? 'text-blue-600 bg-blue-50' : 'text-slate-400 hover:text-slate-600'}`}>
              <Ico d={IC.note} size={16} />
            </button>
            <button onClick={() => setShowSettings((p) => !p)}
              className={`p-2 rounded-lg ${showSettings ? 'text-indigo-600 bg-indigo-50' : 'text-slate-400 hover:text-slate-600'}`}>
              <Ico d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" size={16} />
            </button>
          </div>
        </header>

        {/* SETTINGS PANEL */}
        {showSettings && (
          <>
            <div className="fixed inset-0 z-30" onClick={() => setShowSettings(false)} />
            <div className="absolute right-4 z-40 w-72 rounded-2xl shadow-2xl border p-5 flex flex-col gap-5"
              style={{ top: 58, background: th.headerBg, borderColor: th.border }}>
              <div>
                <p className="text-xs font-black uppercase tracking-widest mb-2.5" style={{ color: th.text, opacity: 0.5 }}>Theme</p>
                <div className="flex gap-2">
                  {[{ key: 'light', label: 'Light', bg: '#ffffff', fg: '#1e293b' }, { key: 'dark', label: 'Dark', bg: '#1e293b', fg: '#e2e8f0' }, { key: 'sepia', label: 'Reading', bg: '#faf6f0', fg: '#3d2b1f' }].map((t) => (
                    <button key={t.key} type="button" onClick={() => setSettings((p) => ({ ...p, theme: t.key }))}
                      className="flex-1 py-2 rounded-xl text-xs font-bold border-2 transition-all"
                      style={{ background: t.bg, color: t.fg, borderColor: settings.theme === t.key ? '#6366f1' : 'transparent', boxShadow: settings.theme === t.key ? '0 0 0 2px #6366f133' : 'none' }}>
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-black uppercase tracking-widest" style={{ color: th.text, opacity: 0.5 }}>Font Size</p>
                  <span className="text-xs font-bold" style={{ color: '#6366f1' }}>{settings.fontSize}px</span>
                </div>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => setSettings((p) => ({ ...p, fontSize: Math.max(12, p.fontSize - 1) }))} className="w-8 h-8 rounded-lg border flex items-center justify-center font-bold text-lg" style={{ borderColor: th.border }}>−</button>
                  <input type="range" min={12} max={24} value={settings.fontSize} onChange={(e) => setSettings((p) => ({ ...p, fontSize: Number(e.target.value) }))} className="flex-1 accent-indigo-600" />
                  <button type="button" onClick={() => setSettings((p) => ({ ...p, fontSize: Math.min(24, p.fontSize + 1) }))} className="w-8 h-8 rounded-lg border flex items-center justify-center font-bold text-lg" style={{ borderColor: th.border }}>+</button>
                </div>
              </div>
              <div>
                <p className="text-xs font-black uppercase tracking-widest mb-2.5" style={{ color: th.text, opacity: 0.5 }}>Font</p>
                <div className="flex gap-2">
                  {[{ key: 'sans', label: 'Sans', style: "'Inter', sans-serif" }, { key: 'serif', label: 'Serif', style: "'Georgia', serif" }, { key: 'mono', label: 'Mono', style: "'Fira Code', monospace" }].map((f) => (
                    <button key={f.key} type="button" onClick={() => setSettings((p) => ({ ...p, fontFamily: f.key }))}
                      className="flex-1 py-2 rounded-xl text-xs font-bold border-2 transition-all"
                      style={{ fontFamily: f.style, borderColor: settings.fontFamily === f.key ? '#6366f1' : th.border, background: settings.fontFamily === f.key ? '#6366f115' : 'transparent', color: th.text }}>
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}

        {/* BODY */}
        <div className="flex flex-1 min-h-0 relative overflow-hidden">

          {/* TOC */}
          {tocOpen && (
            <>
              <div className="fixed inset-0 z-30 bg-black bg-opacity-40 md:hidden" onClick={() => setTocOpen(false)} />
              <div className="fixed left-0 top-[119px] bottom-0 z-40 w-72 md:static md:top-auto md:bottom-auto md:z-auto md:h-full md:w-64 md:flex-shrink-0">
                <EbookTOC book={book} chapters={chapters} activeTopic={activeTopic} expandedChapters={expandedChapters} setExpandedChapters={setExpandedChapters}
                  onSelectTopic={(t) => { selectTopic(t); if (window.innerWidth < 768) setTocOpen(false); }} loading={loadingToc} />
              </div>
            </>
          )}

          {/* MAIN */}
          <main className="flex-1 flex flex-col min-w-0 overflow-hidden" style={{ background: th.mainBg }}>

            {activeTopic && audioUrl && (
              <div className="flex-shrink-0 border-b" style={{ borderColor: th.border }}>
                <div className="flex items-center gap-2 px-4 py-2">
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 18V5l12-2v13" />
                      <circle cx="6" cy="18" r="3" />
                      <circle cx="18" cy="16" r="3" />
                    </svg>
                    <span className="text-xs font-bold hidden sm:inline" style={{ color: '#6366f1' }}>Listen</span>
                  </div>
                  <AudioPlayer audioUrl={audioUrl} timings={timings} onTimeUpdate={handleTimeUpdate} theme={th} />
                </div>
              </div>
            )}

            {/* Scrollable content */}
            <div ref={contentRef} className="flex-1 overflow-y-auto relative select-text" style={{ background: th.mainBg, color: th.text }}>
              {selectionMenu && (
                <div className="absolute z-30 flex items-center gap-1 bg-slate-900 rounded-xl shadow-2xl p-1.5"
                  style={{ top: selectionMenu.top, left: selectionMenu.left, transform: 'translateX(-50%)' }}>
                  {HL_COLORS.map((c) => (
                    <button key={c} title="Highlight" onMouseDown={(e) => e.preventDefault()}
                      onClick={() => { createHighlight(selectedText, c); setSelectionMenu(null); window.getSelection()?.removeAllRanges(); }}
                      className="w-5 h-5 rounded-full border border-white/25 hover:scale-110 transition-transform"
                      style={{ background: c }} />
                  ))}
                  <span className="w-px h-4 bg-white/20 mx-0.5" />
                  <button onMouseDown={(e) => e.preventDefault()}
                    onClick={() => { setDrawer('notes'); setSelectionMenu(null); }}
                    className="px-2 py-1.5 rounded-lg text-white text-xs font-bold hover:bg-blue-500">Note</button>
                  <button onMouseDown={(e) => e.preventDefault()}
                    onClick={() => { navigator.clipboard?.writeText(selectedText); setSelectionMenu(null); }}
                    className="px-2 py-1.5 rounded-lg text-white text-xs font-bold hover:bg-slate-600">Copy</button>
                </div>
              )}

              {loadingContent ? (
                <div className="flex flex-col gap-4 p-6 max-w-4xl mx-auto">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className={`h-4 bg-slate-100 rounded animate-pulse ${i === 2 ? 'w-3/4' : i === 4 ? 'w-5/6' : 'w-full'}`} />
                  ))}
                </div>
              ) : !activeTopic ? (
                <div className="flex flex-col items-center justify-center h-full py-16 text-center px-6">
                  <Ico d={IC.book} size={48} cls="text-slate-200 mb-4" />
                  <p className="font-semibold text-slate-500">Select a topic to start reading</p>
                  <button onClick={() => setTocOpen(true)} className="mt-3 text-xs text-indigo-600 font-bold underline md:hidden">Open contents</button>
                </div>
              ) : (
                <ContentRenderer content={topicContent} activeIndex={activeIndex} timings={timings} />
              )}
            </div>

            {/* Prev / Next */}
            <div className="flex-shrink-0 flex items-center justify-between px-4 sm:px-6 py-3 border-t" style={{ borderColor: th.border, background: th.mainBg }}>
              <button onClick={goPrev} disabled={topicIndex <= 0}
                className="flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-xl border text-sm font-bold disabled:opacity-40 hover:bg-slate-50 transition-colors"
                style={{ borderColor: th.border, color: th.text }}>
                <Ico d={IC.chevL} size={15} /><span className="hidden sm:inline">Prev</span>
              </button>
              <span className="text-xs" style={{ color: th.text, opacity: 0.45 }}>
                {activeTopic ? `${topicIndex + 1} / ${allTopics.length}` : '—'}
              </span>
              <button onClick={goNext} disabled={topicIndex >= allTopics.length - 1 || !activeTopic}
                className="flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 disabled:opacity-40 transition-colors">
                <span className="hidden sm:inline">Next</span><Ico d={IC.chevR} size={15} />
              </button>
            </div>
          </main>

          {/* RIGHT DRAWER */}
          {drawer && (
            <>
              <div className="fixed inset-0 z-30 bg-black bg-opacity-20" onClick={() => setDrawer(null)} />
              <aside className="fixed right-0 z-40 w-full sm:w-80 bg-white border-l border-slate-200 shadow-2xl flex flex-col overflow-hidden" style={{ top: 119, bottom: 0 }}>
                {drawer === 'ai'         && <AIDrawer        onClose={() => setDrawer(null)} selectedText={selectedText} activeTopic={activeTopic} />}
                {drawer === 'highlights' && <HighlightsPanel onClose={() => setDrawer(null)} highlights={highlights} onDelete={(id) => { setHighlights((p) => p.filter((h) => h.id !== id)); if (!isTmp(id)) adminServices.deleteStudentHighlight(id).catch(() => {}); }} />}
                {drawer === 'notes'      && <NotesPanel      onClose={() => setDrawer(null)} notes={notes} onCreateNote={createNote} onDeleteNote={(id) => { setNotes((p) => p.filter((n) => n.id !== id)); if (!isTmp(id)) adminServices.deleteStudentNote(id).catch(() => {}); }} />}
                {drawer === 'bookmarks'  && <BookmarksPanel  onClose={() => setDrawer(null)} bookmarks={bookmarks} onNavigate={(b) => { const t = allTopics.find((x) => String(x.topicId) === String(b.topicId)); if (t) { selectTopic(t); setDrawer(null); } }} />}
              </aside>
            </>
          )}
        </div>

        {/* MODALS */}
        {showAnalytics && <AnalyticsModal onClose={() => setShowAnalytics(false)} analytics={analytics} />}
        {showCompletion && (
          <CompletionModal topic={activeTopic} onClose={() => setShowCompletion(false)}
            onNextTopic={() => { setShowCompletion(false); goNext(); }}
            onAssessment={() => setShowCompletion(false)} />
        )}

        {/* DESKTOP FABs */}
        <div className="hidden sm:flex fixed bottom-6 right-6 flex-col gap-2 z-20">
          <button onClick={toggleBookmark} className={`w-10 h-10 rounded-xl shadow-lg flex items-center justify-center ${isBookmarked ? 'bg-amber-500 text-white' : 'bg-white border border-slate-300 text-slate-500 hover:text-amber-500'}`}>
            <Ico d={IC.star} size={16} />
          </button>
          <button onClick={() => openDrawer('notes')} className="w-10 h-10 rounded-xl bg-white border border-slate-300 shadow-lg flex items-center justify-center text-slate-500 hover:text-indigo-600">
            <Ico d={IC.note} size={16} />
          </button>
        </div>

        {/* MOBILE BOTTOM TAB BAR */}
        <div className="sm:hidden flex-shrink-0 flex items-center justify-around border-t border-slate-200 bg-white px-2 py-2">
          <button onClick={() => setTocOpen((p) => !p)} className={`flex flex-col items-center gap-0.5 px-3 py-1 rounded-xl text-xs font-bold ${tocOpen ? 'text-indigo-600' : 'text-slate-500'}`}>
            <Ico d={IC.list} size={20} /><span>Contents</span>
          </button>
          <button onClick={() => openDrawer('highlights')} className={`flex flex-col items-center gap-0.5 px-3 py-1 rounded-xl text-xs font-bold ${drawer === 'highlights' ? 'text-amber-600' : 'text-slate-500'}`}>
            <Ico d={IC.highlight} size={20} /><span>Highlights</span>
          </button>
          <button onClick={() => openDrawer('notes')} className={`flex flex-col items-center gap-0.5 px-3 py-1 rounded-xl text-xs font-bold ${drawer === 'notes' ? 'text-blue-600' : 'text-slate-500'}`}>
            <Ico d={IC.note} size={20} /><span>Notes</span>
          </button>
        </div>

      </div>
    </Page>
  );
}